import { useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { MainLayout } from '@/layouts/MainLayout';
import { Container, Stack, Button, Alert, PageHeader } from '@/components/ui';
import { RotateCcw } from 'lucide-react';
import { usePermissions } from '@/permissions/hooks';
import { UserRole } from '@/permissions/types';
import { useDashboardLayoutStore, type WidgetId } from '@/store/dashboardLayoutStore';
import { DraggableWidget } from '@/components/dashboard/DraggableWidget';
import { FixedDaysWidget } from '@/components/dashboard/FixedDaysWidget';
import { RecentFixturesWidget } from '@/components/dashboard/RecentFixturesWidget';
import { CargoCountWidget } from '@/components/dashboard/CargoCountWidget';
import { BalticWidget } from '@/components/dashboard/BalticWidget';

// Widget configuration
const WIDGET_CONFIG = {
  fixedDays: {
    id: 'fixedDays' as WidgetId,
    title: 'Fixed Days',
    permission: UserRole.FIXED_DAYS,
    component: FixedDaysWidget,
  },
  recentFixtures: {
    id: 'recentFixtures' as WidgetId,
    title: 'Recent Fixtures',
    permission: UserRole.RECENT_FIXTURES,
    component: RecentFixturesWidget,
  },
  cargoCount: {
    id: 'cargoCount' as WidgetId,
    title: 'Cargo Count',
    permission: UserRole.CARGO_COUNT,
    component: CargoCountWidget,
  },
  baltic: {
    id: 'baltic' as WidgetId,
    title: 'Baltic',
    permission: UserRole.BALTIC,
    component: BalticWidget,
  },
} as const;

export const HomePage = () => {
  const { hasPermission, user, isAuthenticated } = usePermissions();
  const { widgetOrder, saveLayout, resetLayout } = useDashboardLayoutStore();
  const [isMobile, setIsMobile] = useState(false);

  // Check mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Filter widgets based on permissions
  const visibleWidgets = useMemo(() => {
    return widgetOrder.filter((widgetId) => {
      const widget = WIDGET_CONFIG[widgetId];
      return hasPermission([widget.permission]);
    });
  }, [widgetOrder, hasPermission]);

  // Drag and drop sensors (disabled on mobile)
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = visibleWidgets.indexOf(active.id as WidgetId);
      const newIndex = visibleWidgets.indexOf(over.id as WidgetId);

      const newOrder = arrayMove(visibleWidgets, oldIndex, newIndex);

      // Merge with hidden widgets to maintain full order
      const fullOrder = widgetOrder.map((id) =>
        visibleWidgets.includes(id) ? newOrder[visibleWidgets.indexOf(id)] : id
      );

      saveLayout(fullOrder);
    }
  };

  // Handle reset layout
  const handleResetLayout = () => {
    resetLayout();
  };

  // Show loading state while user data is being fetched
  if (isAuthenticated && !user) {
    return (
      <MainLayout>
        <Container size="full">
          <div className="flex h-64 items-center justify-center">
            <div className="text-center">
              <div className="mb-4 inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary-600 border-r-transparent"></div>
              <p className="text-gray-500 dark:text-gray-400">Loading dashboard...</p>
            </div>
          </div>
        </Container>
      </MainLayout>
    );
  }

  // Show welcome message if no permissions
  if (visibleWidgets.length === 0) {
    return (
      <MainLayout>
        <Container size="full">
          <Stack spacing={6}>
            <PageHeader title="Dashboard" />

            <Alert variant="warning" title="No Dashboard Access">
              <p>
                You don't have permission to view any dashboard widgets. Please contact your
                administrator to request access to:
              </p>
              <ul className="mt-2 list-inside list-disc space-y-1">
                <li>Fixed Days</li>
                <li>Recent Fixtures</li>
                <li>Cargo Count</li>
                <li>Baltic Exchange Data</li>
              </ul>
              <p className="mt-3 text-sm">
                For assistance, please email{' '}
                <a
                  href="mailto:development@boel-it.dk"
                  className="text-primary-600 hover:underline"
                >
                  development@boel-it.dk
                </a>
              </p>
            </Alert>
          </Stack>
        </Container>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <Container size="full">
        <Stack spacing={6}>
          <PageHeader
            title="Dashboard"
            actionsInline
            actions={
              <Button
                variant="outline"
                size="sm"
                onClick={handleResetLayout}
                title="Reset layout"
                leftIcon={<RotateCcw className="h-4 w-4" />}
              >
                <span className="hidden sm:inline">Reset Layout</span>
              </Button>
            }
          />

          {/* Dashboard Widgets with Drag and Drop */}
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={visibleWidgets} strategy={rectSortingStrategy}>
              <div className="columns-1 gap-4 sm:gap-6 md:columns-2">
                {visibleWidgets.map((widgetId) => {
                  const widget = WIDGET_CONFIG[widgetId];
                  const WidgetComponent = widget.component;

                  return (
                    <div key={widget.id} className="mb-4 break-inside-avoid sm:mb-6">
                      <DraggableWidget id={widget.id} isDragDisabled={isMobile} noPadding={true}>
                        <WidgetComponent />
                      </DraggableWidget>
                    </div>
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        </Stack>
      </Container>
    </MainLayout>
  );
};
